/**
 * When the admin app raises an alert (ADR 0016). Core keeps what the rules need (probe samples,
 * the figures of the last statuses, the host's) and asks `alertsFor` for the alerts; the
 * thresholds are first guesses, named here so they are changed in one place.
 */

/** An installation counts as down after this many failed probes in a row. */
export const DOWN_SAMPLES = 2;
/** A restart or an unexpected error stays an alert this long. */
export const EVENT_ALERT_HOURS = 24;
export const BACKUP_MAX_AGE_HOURS = 26;
export const CERTIFICATE_MIN_DAYS = 14;
export const DISK_MAX_SHARE = 0.85;
export const SWAP_MAX_SHARE = 0.5;
/** Of the container's memory limit. */
export const MEMORY_MAX_SHARE = 0.9;
/** 5xx responses of all requests in the last hour. */
export const SERVER_ERROR_MAX_SHARE = 0.01;

/** Misuse, per hour: failed sign-ins, rate-limit hits, and 401 and 403 responses together. */
export const FAILED_SIGNINS_PER_HOUR = 20;
export const RATE_LIMITED_PER_HOUR = 10;
export const REFUSED_PER_HOUR = 100;
/** Misuse, per day: demo requests this many times the demos opened, from this many requests. */
export const DEMO_REQUESTS_PER_OPENED = 3;
export const DEMO_REQUESTS_MIN = 10;
/** Misuse, per garage and UTC day. */
export const INVITATIONS_PER_GARAGE = 20;
export const UPLOAD_BYTES_PER_GARAGE = 500_000_000;

/** A release made a procedure fail more: this many errors, at this many times the rate before. */
export const RELEASE_MIN_ERRORS = 3;
export const RELEASE_ERROR_FACTOR = 2;
/** A release made a procedure slower: its p95 this many times as high, and this many ms more. */
export const RELEASE_P95_FACTOR = 1.5;
export const RELEASE_P95_MIN_MS = 50;
/** Calls a procedure needs on both versions before its p95s are compared. */
export const RELEASE_MIN_CALLS = 200;

/** A probe's result: the internal one over the Compose network, the external one through TLS. */
export interface ProbeSample {
  at: string;
  up: boolean;
}

/** What core knows about one installation. */
export interface InstallationHealth {
  name: string;
  /** Oldest first. */
  internal: readonly ProbeSample[];
  /** Oldest first. */
  external: readonly ProbeSample[];
  /** When its server last started, from its status. */
  startedAt: string | null;
  /** Days until its certificate expires, from the last external probe. */
  certificateDaysLeft: number | null;
  /** The container's memory, from its last status; null outside a container. */
  memory: { currentBytes: number; limitBytes: number } | null;
  /** Requests and 5xx responses of the last hour, from the statuses' differences. */
  lastHour: { requests: number; serverErrors: number };
  /** Unexpected errors in the last `EVENT_ALERT_HOURS`. */
  unexpectedErrors: number;
  /** Live demo garages against the most there may be, in the public demo; else null. */
  demo: { live: number; max: number } | null;
  /** Misuse signals, from the statuses' differences, when core has them. */
  misuse?: MisuseHealth;
  /** Each procedure on the version running now and on the one before, after a new version. */
  release?: readonly ReleaseProcedure[];
}

export interface MisuseHealth {
  /** The last hour. */
  failedSignIns: number;
  rateLimited: number;
  /** 401 and 403 responses. */
  refused: number;
  /** The last 24 hours. */
  demoRequested: number;
  demoOpened: number;
  /** The most any one garage did today (UTC). */
  maxInvitationsPerGarage: number;
  maxUploadBytesPerGarage: number;
}

/** A procedure's figures on one version. */
export interface VersionFigures {
  calls: number;
  unexpected: number;
  p95Ms: number;
}

export interface ReleaseProcedure {
  path: string;
  now: VersionFigures;
  before: VersionFigures;
}

/** The host's figures; each is null where it can't be read, e.g. on a developer's Mac. */
export interface HostHealth {
  disk: { usedBytes: number; totalBytes: number } | null;
  swap: { usedBytes: number; totalBytes: number } | null;
  backup: { dumpAt: string | null; offsiteAt: string | null; offsiteConfigured: boolean } | null;
}

export interface HealthState {
  installations: readonly InstallationHealth[];
  host: HostHealth;
  coreDatabaseUp: boolean;
}

export const ALERT_KINDS = [
  "down",
  "internal_only",
  "restarted",
  "backup_old",
  "offsite_old",
  "certificate_expiring",
  "disk_full",
  "swap_used",
  "memory_high",
  "server_errors",
  "unexpected_error",
  "core_database_down",
  "demo_at_cap",
  "failed_signins",
  "rate_limited",
  "refused",
  "demo_requests",
  "invitations",
  "uploads",
  "release_errors",
  "release_slower",
] as const;
export type AlertKind = (typeof ALERT_KINDS)[number];

export interface Alert {
  kind: AlertKind;
  /** Critical when people can't use an installation, or core can't keep figures. */
  severity: "critical" | "warning";
  /** The installation it concerns, or null for the host and core. */
  installation: string | null;
  /** Misuse alerts are marked as such on the overview. */
  misuse: boolean;
  /** What in the installation it concerns: a procedure for the release alerts. */
  subject?: string;
  /** The figure that crossed its threshold, in the threshold's unit, when there is one. */
  value?: number;
  threshold?: number;
}

const HOUR = 3_600_000;

const hoursSince = (at: string, now: Date) => (now.getTime() - Date.parse(at)) / HOUR;

/** Whether the last `DOWN_SAMPLES` samples all failed. */
function downFor(samples: readonly ProbeSample[]): boolean {
  const last = samples.slice(-DOWN_SAMPLES);
  return last.length === DOWN_SAMPLES && last.every((sample) => !sample.up);
}

function installationAlerts(health: InstallationHealth, now: Date): Alert[] {
  const alerts: Alert[] = [];
  const raise = (
    alert: Omit<Alert, "installation" | "severity" | "misuse">,
    { critical = false, misuse = false } = {},
  ) =>
    alerts.push({
      ...alert,
      installation: health.name,
      severity: critical ? "critical" : "warning",
      misuse,
    });

  if (downFor(health.internal)) raise({ kind: "down" }, { critical: true });
  else if (downFor(health.external)) raise({ kind: "internal_only" }, { critical: true });
  if (health.startedAt && hoursSince(health.startedAt, now) < EVENT_ALERT_HOURS) {
    raise({ kind: "restarted" });
  }
  if (health.certificateDaysLeft !== null && health.certificateDaysLeft < CERTIFICATE_MIN_DAYS) {
    raise({
      kind: "certificate_expiring",
      value: health.certificateDaysLeft,
      threshold: CERTIFICATE_MIN_DAYS,
    });
  }
  if (health.memory && health.memory.limitBytes > 0) {
    const share = health.memory.currentBytes / health.memory.limitBytes;
    if (share > MEMORY_MAX_SHARE) {
      raise({ kind: "memory_high", value: share, threshold: MEMORY_MAX_SHARE });
    }
  }
  const { requests, serverErrors } = health.lastHour;
  if (requests > 0 && serverErrors / requests > SERVER_ERROR_MAX_SHARE) {
    raise({
      kind: "server_errors",
      value: serverErrors / requests,
      threshold: SERVER_ERROR_MAX_SHARE,
    });
  }
  if (health.unexpectedErrors > 0)
    raise({ kind: "unexpected_error", value: health.unexpectedErrors });
  if (health.demo && health.demo.live >= health.demo.max) {
    raise({ kind: "demo_at_cap", value: health.demo.live, threshold: health.demo.max });
  }

  const { misuse } = health;
  if (misuse) {
    const over = (kind: AlertKind, value: number, threshold: number) => {
      if (value > threshold) raise({ kind, value, threshold }, { misuse: true });
    };
    over("failed_signins", misuse.failedSignIns, FAILED_SIGNINS_PER_HOUR);
    over("rate_limited", misuse.rateLimited, RATE_LIMITED_PER_HOUR);
    over("refused", misuse.refused, REFUSED_PER_HOUR);
    over("invitations", misuse.maxInvitationsPerGarage, INVITATIONS_PER_GARAGE);
    over("uploads", misuse.maxUploadBytesPerGarage, UPLOAD_BYTES_PER_GARAGE);
    const { demoRequested, demoOpened } = misuse;
    if (
      demoRequested >= DEMO_REQUESTS_MIN &&
      demoRequested > DEMO_REQUESTS_PER_OPENED * demoOpened
    ) {
      raise(
        {
          kind: "demo_requests",
          value: demoRequested,
          threshold: DEMO_REQUESTS_PER_OPENED * demoOpened,
        },
        { misuse: true },
      );
    }
  }

  for (const { path, now: after, before } of health.release ?? []) {
    const rate = (v: VersionFigures) => (v.calls > 0 ? v.unexpected / v.calls : 0);
    if (
      after.unexpected >= RELEASE_MIN_ERRORS &&
      rate(after) > RELEASE_ERROR_FACTOR * rate(before)
    ) {
      raise({ kind: "release_errors", subject: path, value: rate(after), threshold: rate(before) });
    }
    if (
      after.calls >= RELEASE_MIN_CALLS &&
      before.calls >= RELEASE_MIN_CALLS &&
      after.p95Ms > RELEASE_P95_FACTOR * before.p95Ms &&
      after.p95Ms - before.p95Ms >= RELEASE_P95_MIN_MS
    ) {
      raise({ kind: "release_slower", subject: path, value: after.p95Ms, threshold: before.p95Ms });
    }
  }
  return alerts;
}

function hostAlerts(host: HostHealth, now: Date): Alert[] {
  const alerts: Alert[] = [];
  const raise = (alert: Omit<Alert, "installation" | "severity" | "misuse">) =>
    alerts.push({ ...alert, installation: null, severity: "warning", misuse: false });

  const { backup, disk, swap } = host;
  if (backup) {
    const dumpAge = backup.dumpAt === null ? null : hoursSince(backup.dumpAt, now);
    if (dumpAge === null || dumpAge > BACKUP_MAX_AGE_HOURS) {
      raise({ kind: "backup_old", value: dumpAge ?? undefined, threshold: BACKUP_MAX_AGE_HOURS });
    }
    const offsiteAge = backup.offsiteAt === null ? null : hoursSince(backup.offsiteAt, now);
    if (backup.offsiteConfigured && (offsiteAge === null || offsiteAge > BACKUP_MAX_AGE_HOURS)) {
      raise({
        kind: "offsite_old",
        value: offsiteAge ?? undefined,
        threshold: BACKUP_MAX_AGE_HOURS,
      });
    }
  }
  if (disk && disk.totalBytes > 0 && disk.usedBytes / disk.totalBytes > DISK_MAX_SHARE) {
    raise({
      kind: "disk_full",
      value: disk.usedBytes / disk.totalBytes,
      threshold: DISK_MAX_SHARE,
    });
  }
  if (swap && swap.totalBytes > 0 && swap.usedBytes / swap.totalBytes > SWAP_MAX_SHARE) {
    raise({
      kind: "swap_used",
      value: swap.usedBytes / swap.totalBytes,
      threshold: SWAP_MAX_SHARE,
    });
  }
  return alerts;
}

/** The alerts for the state core is in at `now`: critical ones first. */
export function alertsFor(state: HealthState, now: Date): Alert[] {
  const alerts = [
    ...(state.coreDatabaseUp
      ? []
      : [
          {
            kind: "core_database_down",
            severity: "critical",
            installation: null,
            misuse: false,
          } as const,
        ]),
    ...state.installations.flatMap((health) => installationAlerts(health, now)),
    ...hostAlerts(state.host, now),
  ];
  return [
    ...alerts.filter((a) => a.severity === "critical"),
    ...alerts.filter((a) => a.severity !== "critical"),
  ];
}
